<script setup lang="ts">
import { computed, onMounted, ref } from "vue";

import actions from "~/actions";
import { ChevronDownIcon, ChevronUpIcon } from "~/icons";
import { NumberFormat } from "~/shared/enums";
import type { PropertyDefaultSize, PropertyNumber } from "~/shared/types";
import { makeUuid, sanitizeIntegerInput } from "~/utils/common";
import { when } from "~/utils/wait";

const props = defineProps<{
  value: number | null;
  property: PropertyNumber | PropertyDefaultSize;
  reducedPadding?: boolean;
}>();

const emit = defineEmits<{
  update: [value: number | null];
}>();

const textInput = ref<HTMLInputElement | null>(null);

const isDollars = computed(() => props.property.adtl.format === NumberFormat.DOLLARS);
const isPercentage = computed(() => props.property.adtl.format === NumberFormat.PERCENTAGE);

const onInput = (e: Event) => {
  const value = sanitizeIntegerInput(e);
  emit("update", value === "" || value === "-" ? null : parseInt(value, 10));
};

const increment = () => {
  emit("update", (props.value ?? 0) + 1);
};

const decrement = () => {
  emit("update", (props.value ?? 0) - 1);
};

onMounted(async () => {
  actions.visualization.openKeyboardIfIos();
  await when(textInput);
  textInput.value?.focus();
});

const id = ref(`number-editor-input-${makeUuid()}`);
</script>

<template>
  <form class="relative flex items-center" autocomplete="off" @submit.prevent>
    <span v-if="isDollars && value !== null" class="pl-2">$</span>
    <label :for="id" class="sr-only">Value</label>
    <input
      :id="id"
      ref="textInput"
      type="text"
      inputmode="numeric"
      :value="props.value"
      placeholder="None"
      class="size-full border-none border-md bg-transparent pr-2.5 text-sm text-hvy focus-ring-none focus-visible-ring-md placeholder:text-vlt focus-visible:border-md"
      :class="[isDollars ? 'pl-1' : 'pl-2.5', reducedPadding ? 'pt-[5px] pb-1' : 'py-1.5']"
      @keydown.stop
      @input="onInput" />
    <div class="absolute inset-y-0 right-2.5 flex items-center gap-1.5 select-none">
      <span v-if="isPercentage && value !== null">%</span>
      <div class="flex-col justify-center">
        <div class="cursor-pointer rounded-sm hover:bg-md" @click="increment" @keydown.enter="increment">
          <ChevronUpIcon class="size-3" />
        </div>
        <div class="cursor-pointer rounded-sm hover:bg-md" @click="decrement" @keydown.enter="decrement">
          <ChevronDownIcon class="size-3" />
        </div>
      </div>
    </div>
  </form>
</template>
